import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import styled from "@emotion/styled";
import { useSearchParams } from "react-router-dom";
import LatestPortfolio from "component/lastest-portfolio/portfolio/LatestPortfolio";
import Footer from "component/footer/Footer";
import SearchBar from "component/search/SearchBar";
import { PublicPortfolio } from "types/public";
import { getStartups, ApiStage } from "lib/latticeApi";
import PortfolioCard from "component/portfolio-card/PortfolioCard";
import DisplayType from "utils/displayType";
import Banner from "component/banner/Banner";
import ModalLayout from "admin/component/modal/ModalLayout";
import { Pagination } from "@mui/material";
import DefaultButton from "admin/component/button/DefaultButton";
import { updateTitle } from "utils/commonUtil";
import { useLanguage } from "i18n/LanguageContext";
import SectorList from "./organism/SectorList";
import StageList from "./organism/StageList";
import PortfolioModal from "./organism/PortfolioModal";

const DESKTOP_LIMIT = 15;
const MOBILE_LIMIT = 8;

const Portfolio = () => {
  const mode = DisplayType();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  const [randomSeed] = useState(() => Math.floor(Math.random() * 2147483647));
  const selectedPortfolioId = useRef<string>("");
  const [portfolioModalOpen, updatePortfolioModalOpen] =
    useState<boolean>(false);

  // 필터 UI용 메타 데이터
  const [stages, setStages] = useState<ApiStage[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [headerPortfolioList, setHeaderPortfolioList] = useState<
    PublicPortfolio[]
  >([]);

  // 서버 데이터
  const [portfolioList, setPortfolioList] = useState<PublicPortfolio[]>([]);
  const [mobileAccumulated, setMobileAccumulated] = useState<PublicPortfolio[]>(
    []
  );
  const [totalPage, setTotalPage] = useState<number>(0);

  // 쿼리스트링에서 파라미터 읽기
  const currentPage = Number(searchParams.get("page") || "1");
  const searchKeyword = searchParams.get("search") || "";
  const sectorParam = searchParams.get("sector") || "";
  const stageParam = searchParams.get("stage") || "";
  const selectedSectors = sectorParam
    ? sectorParam.split(",").filter(Boolean)
    : [];
  const selectedStageSlugs = stageParam
    ? stageParam.split(",").filter(Boolean)
    : [];
  const limit = mode === "Mobile" ? MOBILE_LIMIT : DESKTOP_LIMIT;

  const updateParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      // 필터 변경 시 page 리셋
      if (key !== "page") {
        next.delete("page");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onClickPortfolio = (id: string) => {
    selectedPortfolioId.current = id;
    updatePortfolioModalOpen(true);
  };

  // 서버에서 데이터 fetch
  const fetchPortfolios = useCallback(async () => {
    try {
      const {
        startups,
        stages: fetchedStages,
        industries: fetchedIndustries,
        pagination,
      } = await getStartups({
        page: currentPage,
        limit,
        search: searchKeyword || undefined,
        industry: sectorParam || undefined,
        stage: stageParam || undefined,
        random: true,
        random_seed: randomSeed,
      });

      const STAGE_LABEL: Record<string, string> = {
        seed: "Seed",
        venture_growth: "Venture & Growth",
        alumni: "Alumni",
      };
      const stageMap = new Map(fetchedStages.map((s) => [s.id, s.name]));
      const enriched = startups.map((s) => ({
        ...s,
        stage:
          (s.stage_id && stageMap.get(s.stage_id)) ||
          STAGE_LABEL[s.stage || ""] ||
          s.stage ||
          "",
      }));

      setPortfolioList(enriched);
      setTotalPage(pagination.total_pages);
      setStages(fetchedStages);
      setIndustries(fetchedIndustries);

      // 모바일 누적
      if (mode === "Mobile" && currentPage > 1) {
        setMobileAccumulated((prev) => [...prev, ...enriched]);
      } else {
        setMobileAccumulated(enriched);
      }
    } catch (error) {
      console.log(error);
    }
  }, [
    currentPage,
    limit,
    searchKeyword,
    sectorParam,
    stageParam,
    mode,
    randomSeed,
  ]);

  // 헤더 포트폴리오 (최신 등록순)
  const fetchHeaderPortfolios = useCallback(async () => {
    try {
      const { startups, stages: fetchedStages } = await getStartups({
        limit: 12,
        order: "created_at",
        dir: "desc",
      });
      const HEADER_STAGE_LABEL: Record<string, string> = {
        seed: "Seed",
        venture_growth: "Venture & Growth",
        alumni: "Alumni",
      };
      const stageMap = new Map(fetchedStages.map((s) => [s.id, s.name]));
      setHeaderPortfolioList(
        startups.map((s) => ({
          ...s,
          stage:
            (s.stage_id && stageMap.get(s.stage_id)) ||
            HEADER_STAGE_LABEL[s.stage || ""] ||
            s.stage ||
            "",
        }))
      );
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  useEffect(() => {
    fetchHeaderPortfolios();
  }, []);

  useLayoutEffect(() => {
    updateTitle(t("portfolio", "pageTitleFull"));
  }, [t]);

  const displayList = mode === "Mobile" ? mobileAccumulated : portfolioList;

  return (
    <Container isModalOpen={portfolioModalOpen}>
      {mode === "Mobile" && (
        <Banner mode={mode}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div className="font-title-en" style={{ color: "var(--white)" }}>
              PORTFOLIO
            </div>
          </div>
        </Banner>
      )}
      <LastPortfolioContainer className="limit__body">
        <LatestPortfolio
          portfolioList={headerPortfolioList}
          onClickPortfolio={onClickPortfolio}
        />
      </LastPortfolioContainer>
      <PortfolioContainer>
        <PortfolioListContainer className="limit__body">
          <div
            className={`portfolio__label ${
              mode === "Mobile" ? "font-sub-title2-en" : "font-title"
            }`}
          >
            Portfolio
          </div>
          <div className="search">
            <SearchBar
              placeholder={t("portfolio", "searchPlaceholder")}
              initValue={searchKeyword}
              onChage={() => {}}
              onSearching={(value) => {
                updateParam("search", value || "");
              }}
            />
          </div>
          <div className="sector">
            <SectorList
              sectors={industries}
              selectedSectors={selectedSectors}
              onSelect={(sector) => {
                const next = selectedSectors.includes(sector)
                  ? selectedSectors.filter((s) => s !== sector)
                  : [...selectedSectors, sector];
                updateParam("sector", next.join(","));
              }}
              onSelectAll={() => updateParam("sector", "")}
            />
          </div>
          <div className="stage">
            <StageList
              stages={stages}
              selectedSlugs={selectedStageSlugs}
              onSelect={(slug) => {
                const next = selectedStageSlugs.includes(slug)
                  ? selectedStageSlugs.filter((s) => s !== slug)
                  : [...selectedStageSlugs, slug];
                updateParam("stage", next.join(","));
              }}
              onSelectAll={() => updateParam("stage", "")}
            />
          </div>

          <div className="portfolio__list">
            {displayList.map((item) => {
              return (
                <PortfolioCard
                  key={item.id}
                  portfolio={item}
                  isViewer
                  onClick={() => {
                    onClickPortfolio(item.id);
                  }}
                />
              );
            })}
            {mode === "Mobile" && displayList.length % 2 === 1 && (
              <PortfolioCard />
            )}
            {displayList.length === 0 && (
              <SearchResultEmpty>
                {mode === "Mobile" ? (
                  <div className="font-body3" style={{ textAlign: "center" }}>
                    {searchKeyword.length > 0
                      ? t("portfolio", "noSearchResultFor").replace(
                          "{{keyword}}",
                          searchKeyword
                        )
                      : t("portfolio", "noSearchResult")}
                  </div>
                ) : (
                  <span className="font-body3">
                    {searchKeyword.length > 0
                      ? t("portfolio", "noSearchResultFor").replace(
                          "{{keyword}}",
                          searchKeyword
                        )
                      : t("portfolio", "noSearchResult")}
                  </span>
                )}
              </SearchResultEmpty>
            )}
          </div>
          {mode !== "Mobile" && totalPage > 1 && (
            <div className="center">
              <Pagination
                count={totalPage}
                shape="rounded"
                page={currentPage}
                onChange={(event, page) => {
                  updateParam("page", String(page));
                }}
              />
            </div>
          )}
        </PortfolioListContainer>
        {mode === "Mobile" && totalPage > 0 && currentPage < totalPage && (
          <div className="center">
            <DefaultButton
              className="font-body3"
              type="outline"
              onClick={() => {
                updateParam("page", String(currentPage + 1));
              }}
            >
              {t("portfolio", "morePortfolio")}
            </DefaultButton>
          </div>
        )}
      </PortfolioContainer>
      <Footer />

      <ModalLayout isOpen={portfolioModalOpen} borderRadius={0}>
        <PortfolioModal
          portfolioId={selectedPortfolioId.current}
          close={() => {
            selectedPortfolioId.current = "";
            updatePortfolioModalOpen(false);
          }}
        />
      </ModalLayout>
    </Container>
  );
};

const Container = styled.div<{ isModalOpen: boolean }>`
  width: 100%;
  height: 100%;
  min-height: 100vh;
  display: flex;
  padding-top: 80px;
  flex-direction: column;
  background-color: var(--white);
  overflow: ${(props) => (props.isModalOpen ? "hidden" : "scroll")};
  @media only screen and (max-width: 768px) {
    padding-top: 0px;
  }
`;

const LastPortfolioContainer = styled.div`
  width: 100%;
  margin-top: 100px;
  margin-bottom: 82px;

  @media only screen and (max-width: 768px) {
    margin-top: 40px;
    margin-bottom: 40px;
  }
`;

const PortfolioContainer = styled.div`
  flex-grow: 1;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding-top: 118px;
  padding-bottom: 160px;
  background-color: var(--bg-02);

  .center {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: transparent;
    padding-bottom: 64px;
  }
  @media only screen and (max-width: 768px) {
    padding-top: 20px;
    padding-bottom: 0px;

    .center {
      background-color: var(--white);
    }
  }
`;

const PortfolioListContainer = styled.div`
  width: 100%;

  .portfolio__label {
    color: var(--black);
    text-transform: uppercase;
  }

  .search {
    margin-top: 64px;
  }
  .sector {
    margin-top: 56px;
  }
  .stage {
    margin-top: 32px;
  }

  .portfolio__list {
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    row-gap: 40px;
    column-gap: 24px;

    margin-top: 56px;
    padding-bottom: 64px;
  }

  @media only screen and (max-width: 768px) {
    .search {
      margin-top: 8px;
    }
    .sector {
      margin-top: 40px;
    }
    .stage {
      margin-top: 24px;
    }
    .portfolio__list {
      width: calc(100% + 48px);
      display: flex;
      transform: translateX(-24px);
      padding: 40px 24px 40px 24px;
      background-color: var(--white);
      margin-top: 0px;
      justify-content: center;
      row-gap: 40px;
      column-gap: 7px;
    }
  }
`;

const SearchResultEmpty = styled.div`
  width: 100%;
  height: 100%;
  min-height: 276px;
  max-height: 276px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-top: 1px solid var(--grey-00);
  border-bottom: 1px solid var(--grey-00);
`;
export default Portfolio;
